import { faBell } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type React from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useNotificationsSheet } from './hooks/useNotificationsSheet';
import { NotificationItem } from './NotificationItem';
import type { NotificationsPopoverProps } from './types';

const noop = () => {};

/** Bell preview — an unfiltered, compact list of notifications. Tasks, tabs
 *  and filtering all live on the /activity-feed page, which the bell opens. */
export function NotificationsPopover({
  open,
  onOpenChange,
  trigger,
  openOnHover = false,
  onTriggerClick,
}: NotificationsPopoverProps) {
  const {
    t,
    allItems,
    grouped,
    unreadCount,
    isLoading,
    isFetchingNextPage,
    isMarkAllPending,
    handleMarkAllRead,
    handleMarkRead,
    bottomRef,
  } = useNotificationsSheet(open);

  const isMobile = useIsMobile();

  const content = (
    <>
      {/* Header */}
      <div className='flex items-center justify-between gap-2 px-3 pt-3 pb-1 shrink-0'>
        <span className='text-xs font-semibold'>{t('tabs.notifications')}</span>
        {unreadCount > 0 && (
          <Button
            variant='ghost'
            size='sm'
            onClick={handleMarkAllRead}
            disabled={isMarkAllPending}
            className='text-muted-foreground hover:text-foreground h-auto py-0.5 px-1.5'
          >
            {t('markAllRead')}
          </Button>
        )}
      </div>

      {/* Feed */}
      <div className='flex-1 overflow-y-auto min-h-0'>
        {isLoading ? (
          <div className='flex flex-col'>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className='flex items-start gap-2 px-3 py-2'>
                <div className='size-7 rounded-lg bg-muted animate-pulse shrink-0' />
                <div className='flex-1 space-y-1.5 pt-0.5'>
                  <div className='h-2.5 bg-muted animate-pulse rounded w-3/4' />
                  <div className='h-2 bg-muted animate-pulse rounded w-1/2' />
                </div>
              </div>
            ))}
          </div>
        ) : allItems.length === 0 ? (
          <div className='flex flex-col items-center justify-center py-8 gap-2 text-center px-5'>
            <div className='size-8 rounded-full bg-muted flex items-center justify-center'>
              <FontAwesomeIcon
                icon={faBell}
                className='size-3.5 text-muted-foreground'
              />
            </div>
            <div className='space-y-0.5'>
              <p className='text-xs font-medium'>{t('empty.notifications')}</p>
              <p className='text-xs text-muted-foreground'>
                {t('empty.notificationsHint')}
              </p>
            </div>
          </div>
        ) : (
          <>
            {grouped.map(({ group, items }) => (
              <div key={group}>
                <div className='px-3 py-1 bg-popover sticky top-0 z-10'>
                  <span className='text-xs font-semibold text-muted-foreground uppercase tracking-wider'>
                    {t(`groups.${group}`)}
                  </span>
                </div>
                {items.map(n => (
                  <NotificationItem
                    key={n.id}
                    notification={n}
                    tab='notifications'
                    onDismiss={noop}
                    onReactivate={noop}
                    onMarkRead={handleMarkRead}
                    dense
                  />
                ))}
              </div>
            ))}
            <div ref={bottomRef} className='py-1.5 flex justify-center'>
              {isFetchingNextPage && (
                <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                  <div className='size-3 border-2 border-current border-t-transparent rounded-full animate-spin' />
                  {t('loading')}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side='right'
          showCloseButton
          className='!w-screen !max-w-screen p-0 flex flex-col overflow-hidden'
        >
          {content}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next, details) => {
        // Pressing the bell opens the full page instead of toggling the
        // preview; hover/escape/outside-press still drive the popover.
        if (onTriggerClick && details.reason === 'trigger-press') {
          onOpenChange(false);
          onTriggerClick();
          return;
        }
        onOpenChange(next);
      }}
    >
      <PopoverTrigger
        openOnHover={openOnHover}
        delay={200}
        closeDelay={120}
        render={trigger as React.ReactElement}
      />
      <PopoverContent
        align='end'
        sideOffset={8}
        className='w-100 p-0 flex flex-col h-140 overflow-hidden'
      >
        {content}
      </PopoverContent>
    </Popover>
  );
}
